<template>
  <div class="serp__filter-filter--container q-py-1 q-shadow-sm q-my-2 q-mb-2 q-rounded-3 q-bg-white ">
    <div class="q-accordion-item">
      <h6
          class="q-accordion-header q-position-relative serp__filter-item--header q-accordion-button q-p-3 q-py-2 q-mt-0 q-fs-6 q-my-0 q-fw-semibold"
          :class="headerButtonClasses"
          @click="toggleAccordion"
          :aria-expanded="accordionAriaExpanded"
          :aria-controls="filter.field"
      >
        <span class="serp__filter-item--title">{{ filter.label }}</span>
        <button
            v-if="accordionClearFiltersButtonEnabled && hasActiveValues"
            type="button"
            class="serp__filter-item--clear-button"
            @click.stop.prevent="clearFilterGroup"
        >
          {{ clearButtonLabel }}
        </button>
      </h6>

      <div
          :id="filter.field"
          class="q-accordion-collapse"
          :class="accordionBodyClasses"
          :aria-labelledby="filter.field"
      >
        <div class="q-accordion-body q-p-1">
          <div class="serp__filter-item--container q-container-fluid" :class="computedClassContainer">
            <ul :class="computedClass" class="q-my-1">

              <li
                  class="q-input-group q-input-group-sm q-my-2 filter-search__container"
                  v-if="accordionSearchEnabled && ((filter.type !== 'range' && filter.type !== 'rangeSlider')
                  && (filter.type !== 'category' && lengthOfAggregate >= 6)
                  || (filter.type === 'category' && lengthOfAggregate >= 2))"
              >
                <input
                    type="text"
                    class="q-form-control filter-search__input"
                    :placeholder="searchPlaceholder"
                    v-model="searchQuery"
                />
              </li>

              <FilterCheckboxRecursive
                  v-if="filter.type === 'checkbox' || filter.type === 'category'"
                  :values="filteredValues"
                  :field="filter.field"
              />

              <BoxItem
                  v-if="filter.type === 'box'"
                  :filterDataValues="filteredValues"
                  :field="filter.field"
              />

              <InputRangeItem
                  v-if="filter.type === 'range'"
                  :filterData="filter.data"
                  :field="filter.field"
              />

              <MultiRangeSlider
                  v-if="filter.type === 'rangeSlider'"
                  :filterData="filter.data"
                  :field="filter.field"
              />
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineProps, ref, inject, watch, unref } from 'vue'
import { CustomSmartSearchLabels, SmartSearchTranslationsInterface } from '@/core/types'

// Podrzędne komponenty
import FilterCheckboxRecursive from './FilterItem/FilterCheckboxRecursive.vue'
import BoxItem from './FilterItem/BoxItem.vue'
import InputRangeItem from './FilterItem/InputRangeItem.vue'
import MultiRangeSlider from './FilterItem/MultiRangeSlider.vue'
import {useFiltersStore} from "@/store/SerpPage/FiltersStore"
import {config} from "@/core/config"
import { resolveCustomSmartSearchConfig } from '@/core/customSmartSearchConfig'

// Props
const props = defineProps(['filter', 'filterCollapsed'])

const sessionStorageKey = `${config.searchId}_filtersAccordionState`

const customSmartSearchConfig = resolveCustomSmartSearchConfig()
const isAccordionModeEnabled = customSmartSearchConfig.accordionMode
const accordionSearchEnabled = customSmartSearchConfig.accordionSearchEnabled
const accordionClearFiltersButtonEnabled = customSmartSearchConfig.accordionClearFiltersButtonEnabled

function readAccordionState(): Record<string, boolean> {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return {}
  }

  try {
    const storedValue = window.sessionStorage.getItem(sessionStorageKey)

    if (!storedValue) {
      return {}
    }

    const parsedValue = JSON.parse(storedValue)

    if (typeof parsedValue !== 'object' || parsedValue === null) {
      return {}
    }

    return parsedValue
  } catch (error) {
    console.warn('Unable to read accordion state from sessionStorage', error)
    return {}
  }
}

function saveAccordionState(field: string, isCollapsed: boolean) {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return
  }

  try {
    const currentState = readAccordionState()
    currentState[field] = isCollapsed
    window.sessionStorage.setItem(sessionStorageKey, JSON.stringify(currentState))
  } catch (error) {
    console.warn('Unable to save accordion state to sessionStorage', error)
  }
}

function getInitialCollapsedState(field: string, defaultValue: boolean): boolean {
  const storedState = readAccordionState()

  if (Object.prototype.hasOwnProperty.call(storedState, field)) {
    return storedState[field]
  }

  return defaultValue
}

const isCollapsed = ref(isAccordionModeEnabled ? getInitialCollapsedState(props.filter.field, props.filterCollapsed || false) : false)

const filtersStore = useFiltersStore()

function toggleAccordion() {
  if (!isAccordionModeEnabled) {
    return
  }

  isCollapsed.value = !isCollapsed.value
}

watch(isCollapsed, (newValue) => {
  if (!isAccordionModeEnabled) {
    return
  }

  saveAccordionState(props.filter.field, newValue)
})

watch(() => props.filter.field, (newField) => {
  if (!isAccordionModeEnabled) {
    return
  }

  isCollapsed.value = getInitialCollapsedState(newField, props.filterCollapsed || false)
})

watch(() => props.filterCollapsed, (newDefault) => {
  if (!isAccordionModeEnabled) {
    isCollapsed.value = false
    return
  }

  const storedState = readAccordionState()

  if (!Object.prototype.hasOwnProperty.call(storedState, props.filter.field)) {
    isCollapsed.value = Boolean(newDefault)
  }
})

const headerButtonClasses = computed(() => ({
  'q-rotatable': isAccordionModeEnabled,
  'q-collapsed': isAccordionModeEnabled && isCollapsed.value,
  'q-rotated': isAccordionModeEnabled && !isCollapsed.value,
  'serp__filter-header--static': !isAccordionModeEnabled
}))

const accordionBodyClasses = computed(() => {
  if (!isAccordionModeEnabled) {
    return 'q-show'
  }

  return {
    'q-collapse': isCollapsed.value,
    'q-show': !isCollapsed.value
  }
})

const accordionAriaExpanded = computed(() => {
  if (!isAccordionModeEnabled) {
    return 'true'
  }

  return (!isCollapsed.value).toString()
})

const lengthOfAggregate = computed(() => props?.filter?.data?.values?.length || 0)


// Tłumaczenia (jak w Twoim kodzie)
const SmartSearchTranslations = inject('SmartSearchTranslations') as any
const customLabels = (typeof window !== 'undefined'
  ? (window as any)?._QONizer?.customSmartSearchConfig?.labels
  : undefined) as CustomSmartSearchLabels | undefined

const normalizeLabel = (label: unknown): string | undefined => {
  if (typeof label !== 'string') {
    return undefined
  }

  const trimmed = label.trim()
  return trimmed || undefined
}

function getFilterSearchHeader(): string {
  const customPrefix = normalizeLabel(customLabels?.filters?.searchPlaceholderPrefix)
  if (customPrefix) {
    return customPrefix
  }

  const prefix = normalizeLabel(unref(SmartSearchTranslations)?.filters?.searchPlaceholderPrefix)

  if (prefix) {
    return prefix
  }

  const base = normalizeLabel(unref(SmartSearchTranslations)?.serp?.filterSearchHeader)

  if (base) {
    return base
  }

  return 'Szukaj w'
}

function getClearLabel(): string {
  const custom = normalizeLabel(customLabels?.filters?.clear)
  if (custom) {
    return custom
  }

  const base = normalizeLabel(unref(SmartSearchTranslations)?.filters?.clear)

  if (base) {
    return base
  }

  return 'Wyczyść filtry'
}

const normalizedFilterLabel = computed(() => {
  const label = props?.filter?.label

  if (typeof label !== 'string') {
    return ''
  }

  try {
    return label.toLocaleLowerCase('pl-PL')
  } catch (error) {
    return label.toLowerCase()
  }
})

const searchPlaceholder = computed(() => {
  const basePlaceholder = getFilterSearchHeader()
  const label = normalizedFilterLabel.value

  if (!label) {
    return basePlaceholder
  }

  const trimmedBase = basePlaceholder?.trim?.() ?? ''

  if (!trimmedBase) {
    return label
  }

  return `${trimmedBase} ${label}`
})

const clearButtonLabel = computed(() => {
  const label = normalizedFilterLabel.value

  if (!label) {
    return getClearLabel()
  }

  const base = getClearLabel()
  const sanitizedBase = typeof base === 'string'
      ? base.replace(/filtr[a-ząćęłńóśźż]*/gi, '').trim()
      : ''

  const prefix = sanitizedBase || (typeof base === 'string' ? base.trim() : '')

  if (!prefix) {
    return label
  }

  return `${prefix} ${label}`
})

const hasActiveValues = computed(() => {
  const activeFilters = filtersStore.getActiveFilters
  const activeValue = activeFilters?.[props.filter.field]

  if (!activeValue) {
    return false
  }

  if (Array.isArray(activeValue)) {
    return activeValue.length > 0
  }

  if (typeof activeValue === 'object') {
    return Object.keys(activeValue).length > 0
  }

  return Boolean(activeValue)
})

function promoteActiveValues(values: any[]): any[] {
  if (!Array.isArray(values) || values.length === 0) {
    return values
  }

  const activeFilters = filtersStore.getActiveFilters
  const activeValues = activeFilters?.[props.filter.field]
  const activeSet = Array.isArray(activeValues) ? new Set(activeValues) : new Set()

  return values
      .map((value) => {
        const clonedValue = {
          ...value,
        }

        if (Array.isArray(value?.aggregations)) {
          clonedValue.aggregations = promoteActiveValues(value.aggregations)
        }

        return clonedValue
      })
      .sort((valueA, valueB) => {
        const valueAIsActive = valueA?.label ? activeSet.has(valueA.label) : false
        const valueBIsActive = valueB?.label ? activeSet.has(valueB.label) : false

        if (valueAIsActive === valueBIsActive) {
          return 0
        }

        return valueAIsActive ? -1 : 1
      })
}

async function clearFilterGroup() {
  await filtersStore.clearFilterByField(props.filter.field)
}

// Klasy CSS do <ul>
const computedClass = computed(() => {
  if (props.filter.type === 'box') {
    return `q-p-0 serp__filter-item--${props.filter.type}`
  }
  return [
    'serp__filter-item--group',
    `serp__filter-item--${props.filter.type}`,
    'q-list-group',
    'q-list-group-flush',
    'q-list-group-collapse',
  ]
})

const computedClassContainer = computed(() => {
  return [`filter__item-container-${props.filter.type}`]
})

// SEARCH
const searchQuery = ref('')

// 1) Definiujemy rekurencyjną funkcję filtrującą
function filterCategoriesRecursive(values: any[], query: string) {
  // jeżeli brak query => nie filtrujemy
  if (!query) {
    return values
  }
  // w przeciwnym wypadku mapujemy i filtrujemy
  return values
      .map(item => {
        const label = typeof item?.label === 'string' ? item.label.toLowerCase() : ''
        const match = label.includes(query)
        const hasAggregations = Array.isArray(item?.aggregations) && item.aggregations.length > 0
        let subItems = []
        if (hasAggregations) {
          subItems = filterCategoriesRecursive(item.aggregations, query)
        }

        // Zostawiamy ten element, jeśli:
        // a) Sam pasuje (match),
        // b) albo jego któryś potomek pasuje (subItems niepuste).
        if (match || subItems.length > 0) {
          // Zwracamy nowy obiekt z *przefiltrowaną* listą subItems
          return {
            ...item,
            aggregations: subItems,
          }
        } else {
          // W przeciwnym wypadku odrzucamy go (undefined)
          return undefined
        }
      })
      // wyrzucamy te, które są undefined
      .filter(Boolean)
}

function filterFlat(values: any[], query: string) {
  if (!query) {
    return values
  }
  return values.filter(item =>
      typeof item?.label === 'string' && item.label.toLowerCase().includes(query)
  )
}

// 2) computed, które zwraca przefiltrowane dane
const filteredValues = computed(() => {
  const allValues = Array.isArray(props.filter?.data?.values) ? props.filter.data.values : []

  if (!Array.isArray(allValues) || allValues.length === 0) {
    return allValues
  }

  const query = (searchQuery.value || '').toLowerCase()
  let processedValues = allValues

  if (props.filter.type === 'category' || props.filter.type === 'checkbox') {
    processedValues = filterCategoriesRecursive(allValues, query)
  } else if (props.filter.type === 'box') {
    processedValues = filterFlat(allValues, query)
  }

  if (props.filter.type === 'checkbox') {
    return promoteActiveValues(processedValues)
  }

  return processedValues
})

</script>

<style>
/* Twój dotychczasowy CSS */
.dropdown-divider__container, .filter-search__container {
  grid-column: 1 / -1;
}

.serp__filter-item--container {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
}

.serp__filter-item--box {
  display: grid !important;
  grid-template-columns: repeat(3, 3fr);
  width: -moz-max-content !important;
  width: max-content !important;
  min-width: 100%;
}

.filter-item__box-text {
  font-size: 0.75em !important;
}

.filter__item-container-rangeSlider {
  min-height: 80px;
}

.q-accordion-item {
  background-color: #fff;
  border: 0px solid rgba(0, 0, 0, .125);
}

.serp__filter-item--header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.serp__filter-item--title {
  flex: 1;
}

.serp__filter-item--clear-button {
  border: none;
  background: transparent;
  color: #9aa0a6;
  font-size: 0.75rem;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  padding-right: 30px;
}

.serp__filter-item--clear-button:hover,
.serp__filter-item--clear-button:focus {
  color: #d33242;
  outline: none;
}

.q-accordion-header {
  cursor: pointer;
}

.q-accordion-header.serp__filter-header--static {
  cursor: default;
}

.serp__filter-header--static .serp__filter-item--clear-button {
  padding-right: 0;
}

.q-accordion-button.q-rotatable::after {
  flex-shrink: 0;
  width: 1.25em;
  height: 1.25em;
  margin-left: auto;
  content: "";
  background-image: url("data:image/svg+xml,%3csvg ...");
  background-repeat: no-repeat;
  background-size: 1.25em;
  display: block;
  position: absolute;
  right: 1em;
  bottom: 0.5em;
  top: 50%;
}

.q-rotatable::after {
  transform: translate(0, -50%) rotate(0deg)!important;
  transition: transform .2s ease-in-out;
}

.q-rotated::after {
  transform: translate(0, -50%) rotate(180deg)!important;
}

.q-form-control.filter-search__input:focus {
  border-color: var(--q-red) !important;
}
</style>
